/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Radio, Alert } from 'antd'
import Icon from 'src/components/Icon'
import Button from 'src/components/ts-components/Button'
import Text from 'src/components/ts-components/Text'
import CreditCardClockIcon from 'src/components/ts-components/icons/CreditCardClockIcon'

const PaymentMethodSelector = ({
  isDeferredPaymentEnabled,
  selectedMethod,
  onChange,
  disabled = false,
  children,
  isPaymentProviderNotSet = false,
  onBack,
  backDisabled = false,
}) => {
  const disabledStyle = css`
    opacity: 0.5;
    cursor: not-allowed;
  `

  const containerStyle = css`
    display: flex;
    padding: 16px;
    background: ${colors.NEUTRAL_600};
    border-radius: 8px;
    border: 1px solid ${colors.NEUTRAL_400};
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 16px;
    align-self: stretch;
    width: 100%;
  `

  const separatorStyle = css`
    align-self: stretch;
    height: 0;
    border: 0.5px solid ${colors.NEUTRAL_400};
  `

  const optionContainerStyle = css`
    width: 100%;
    align-self: stretch;
    justify-content: flex-start;
    align-items: center;
    gap: 16px;
    display: flex;
    cursor: ${disabled ? 'not-allowed' : 'pointer'};
    transition: background-color 0.2s;
    opacity: ${disabled ? 0.5 : 1};
    margin-top: 0 !important;
    margin-left: 0 !important;
  `

  const iconContainerStyle = css`
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  `

  const textContainerStyle = css`
    flex: 1 1 0;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    display: flex;
  `

  const radioGroupStyle = css`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;

    .ant-radio-wrapper {
      display: flex;
      flex-direction: row-reverse;
      align-items: flex-start;
      width: 100%;
      margin: 0;
      padding: 0;

      > span:last-child {
        flex: 1;
      }
    }

    .ant-radio {
      display: flex;
      flex-shrink: 0;
    }

    .ant-radio-wrapper-checked ${optionContainerStyle} {
      background-color: ${colors.NEUTRAL_600};
    }

    .ant-radio-wrapper-disabled {
      cursor: not-allowed;
    }
  `

  const childrenContainerStyle = css`
    max-width: 100%;
    width: 100%;
    margin-top: 8px;
    padding: 16px;
    border-radius: 8px;
    background: ${colors.NEUTRAL_700};
  `

  const paidInPersonContainerStyle = css`
    max-width: 100%;
    width: 100%;
    margin-top: 16px;
    padding: 0;
    border-radius: 0;
    background: transparent;
  `

  const buttonGroupStyle = css`
    display: flex;
    gap: 8px;
    width: 100%;
    margin-top: 16px;
    @media (max-width: 768px) {
      flex-direction: column-reverse;
    }
    & > button {
      flex: 1 1 0;
    }
  `

  const radioOptionStyle = css`
    display: flex;
    flex-direction: column;
    width: 100%;
  `

  // BACK stays reachable even when the provider is disabled — the credit-card
  // children (which normally carry the back button) are suppressed in that case,
  // so render a standalone back button beside the "provider disabled" alert.
  const disabledProviderBackButton =
    isPaymentProviderNotSet && onBack ? (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          width: 100%;
        `}
      >
        <Button
          onClick={onBack}
          text='BACK'
          variant='secondary'
          disabled={backDisabled}
          css={css`
            width: 50%;
            max-width: 200px;
            @media (max-width: 768px) {
              width: 100%;
              max-width: 100%;
            }
          `}
        />
      </div>
    ) : null

  // If deferred payment is not enabled, show only credit card UI
  if (!isDeferredPaymentEnabled) {
    return (
      <div css={containerStyle}>
        <Text variant='paragraphMedium' color={colors.NEUTRAL_0} weight='bold'>
          Payment Method
        </Text>
        <div css={separatorStyle} />
        <div
          css={[optionContainerStyle, isPaymentProviderNotSet && disabledStyle]}
        >
          <div css={iconContainerStyle}>
            <Icon
              name='credit-card'
              color={
                isPaymentProviderNotSet ? colors.NEUTRAL_100 : colors.WHITE
              }
              fontSize={20}
            />
          </div>
          <div css={textContainerStyle}>
            <Text
              variant='paragraphMedium'
              weight='bold'
              color={
                isPaymentProviderNotSet ? colors.NEUTRAL_100 : colors.WHITE
              }
            >
              Credit Card
            </Text>
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={
                isPaymentProviderNotSet ? colors.NEUTRAL_200 : colors.NEUTRAL_50
              }
            >
              Enter your payment details below
            </Text>
          </div>
        </div>
        {!isPaymentProviderNotSet && (
          <div css={childrenContainerStyle}>{children}</div>
        )}
        {isPaymentProviderNotSet && (
          <Alert
            message='Payment provider is disabled. Please contact support for assistance.'
            type='warning'
            showIcon
            css={css`
              margin-top: 16px;
              background: ${colors.WARNING_50};
              border: 1px solid ${colors.WARNING_200};
              .ant-alert-icon {
                color: ${colors.WARNING_400};
              }
              .ant-alert-message {
                color: ${colors.WARNING_400};
                font-weight: 500;
              }
            `}
          />
        )}
        {disabledProviderBackButton}
      </div>
    )
  }

  return (
    <div css={containerStyle}>
      <Text variant='paragraphMedium' color={colors.NEUTRAL_0} weight='bold'>
        Payment Method
      </Text>
      <div css={separatorStyle} />

      <Radio.Group
        css={radioGroupStyle}
        value={selectedMethod}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        <div css={[radioOptionStyle, isPaymentProviderNotSet && disabledStyle]}>
          <Radio value='credit-card' disabled={isPaymentProviderNotSet}>
            <div css={optionContainerStyle}>
              <div css={iconContainerStyle}>
                <Icon
                  name='credit-card'
                  color={
                    isPaymentProviderNotSet ? colors.NEUTRAL_100 : colors.WHITE
                  }
                  fontSize={18}
                />
              </div>
              <div css={textContainerStyle}>
                <Text
                  variant='paragraphMedium'
                  weight='bold'
                  color={
                    isPaymentProviderNotSet ? colors.NEUTRAL_100 : colors.WHITE
                  }
                >
                  Credit Card
                </Text>
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={
                    isPaymentProviderNotSet
                      ? colors.NEUTRAL_200
                      : colors.NEUTRAL_50
                  }
                >
                  Enter your payment details below
                </Text>
              </div>
            </div>
          </Radio>
          {selectedMethod === 'credit-card' && !isPaymentProviderNotSet && (
            <div css={childrenContainerStyle}>{children}</div>
          )}
        </div>

        {isPaymentProviderNotSet && (
          <Alert
            message='Payment Provider is Disabled. Please Contact SportNinja Support (support@sportninja.com) for Assistance.'
            type='warning'
            showIcon
          />
        )}

        {disabledProviderBackButton}

        <div css={separatorStyle} />

        <div css={radioOptionStyle}>
          <Radio value='paid-in-person'>
            <div css={optionContainerStyle}>
              <div css={iconContainerStyle}>
                <CreditCardClockIcon size={24} color={colors.WHITE} />
              </div>
              <div css={textContainerStyle}>
                <Text
                  variant='paragraphMedium'
                  weight='bold'
                  color={colors.WHITE}
                >
                  Defer Payment
                </Text>
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                  css={css`
                    margin-top: 4px;
                  `}
                >
                  Choose if you&apos;ve already paid or arranged another method
                  of payment. Pending administrator approval
                </Text>
              </div>
            </div>
          </Radio>
          {selectedMethod === 'paid-in-person' && (
            <div css={[paidInPersonContainerStyle, buttonGroupStyle]}>
              {children}
            </div>
          )}
        </div>
      </Radio.Group>
    </div>
  )
}

export default PaymentMethodSelector
